import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  type?: 'info' | 'success' | 'warning' | 'error'
  showCloseButton?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const typeConfig = {
  info: {
    icon: Info,
    bg: 'bg-slate-50 dark:bg-slate-800',
    iconColor: 'text-slate-600 dark:text-slate-400',
    border: 'border-slate-200 dark:border-slate-700',
  },
  success: {
    icon: CheckCircle,
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
  },
  error: {
    icon: AlertCircle,
    bg: 'bg-red-50 dark:bg-red-900/20',
    iconColor: 'text-red-600 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
  },
}

const sizeConfig = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
}

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  type = 'info',
  showCloseButton = true,
  size = 'md',
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const config = typeConfig[type]
  const Icon = config.icon

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn"
      onClick={(e) => e.target === overlayRef.current && onClose()}
    >
      <div className={`
        ${sizeConfig[size]} w-full bg-white dark:bg-slate-800 rounded-[20px] shadow-modal 
        border border-gray-200/60 dark:border-slate-700/60
        transform animate-scaleIn
      `}>
        {/* Header */}
        <div className={`
          flex items-center gap-3 px-6 py-4 border-b ${config.border}
          ${config.bg} rounded-t-[20px]
        `}>
          <div className={`p-2 rounded-full ${config.bg}`}>
            <Icon className={`w-5 h-5 ${config.iconColor}`} />
          </div>
          <h3 className="flex-1 text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-6 py-5 text-gray-600 dark:text-gray-300">
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}

// Utility components
interface ModalActionsProps {
  children: React.ReactNode
  className?: string
}

export function ModalActions({ children, className = '' }: ModalActionsProps) {
  return (
    <div className={`flex items-center justify-end gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 ${className}`}>
      {children}
    </div>
  )
}

interface ModalButtonProps {
  onClick: () => void
  children: React.ReactNode
  variant?: 'primary' | 'secondary' | 'danger'
  disabled?: boolean
}

export function ModalButton({ onClick, children, variant = 'secondary', disabled = false }: ModalButtonProps) {
  const variants = {
    primary: 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900 hover:bg-slate-900 dark:hover:bg-white hover:shadow-btn-hover',
    secondary: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600',
    danger: 'bg-red-600 text-white hover:bg-red-700 hover:shadow-btn-hover',
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        h-[44px] px-5 rounded-[12px] font-medium text-sm
        transition-all duration-200 ease-apple
        hover:-translate-y-px active:scale-[0.98]
        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/30
        ${variants[variant]}
      `}
    >
      {children}
    </button>
  )
}
